'use client'

// Primitif tampilan kecil yang dipakai ulang di seluruh view /dev.
import type { TaskStatus, TaskPriority } from '@/lib/types'
import type { RiskKind, Health } from '@/lib/dev-sprint'
import { RISK_LABEL } from '@/lib/dev-sprint'
import { picColor } from '@/lib/utils'

export const STATUS_META: Record<TaskStatus, { label: string; bg: string; text: string; dot: string }> = {
  'not-started': { label: 'To Do', bg: '#F2F1EC', text: '#6B6A65', dot: '#B9B8B2' },
  'in-progress': { label: 'In Progress', bg: '#FFF4DC', text: '#9A6B00', dot: '#E0A32E' },
  done: { label: 'Done', bg: '#E4F5EA', text: '#1E7A46', dot: '#34C77B' },
}

export const PRIORITY_META: Record<TaskPriority, { label: string; color: string }> = {
  low: { label: 'Low', color: '#9AA0A6' },
  medium: { label: 'Medium', color: '#E0A32E' },
  high: { label: 'High', color: '#E5484D' },
}

export const HEALTH_META: Record<Health, { label: string; bg: string; text: string }> = {
  'on-track': { label: 'On Track', bg: '#E4F5EA', text: '#1E7A46' },
  'at-risk': { label: 'At Risk', bg: '#FFF4DC', text: '#9A6B00' },
  behind: { label: 'Behind', bg: '#FCE8E8', text: '#C0362C' },
}

const RISK_COLOR: Record<RiskKind, string> = {
  late: '#C0362C',
  blocked: '#7A3EBE',
  'due-soon': '#9A6B00',
  overload: '#C2410C',
  aging: '#6B6A65',
}

export function StatusPill({ status }: { status: TaskStatus }) {
  const m = STATUS_META[status]
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ backgroundColor: m.bg, color: m.text }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.dot }} />
      {m.label}
    </span>
  )
}

export function PriorityDot({ priority }: { priority: TaskPriority }) {
  const m = PRIORITY_META[priority]
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: m.color }} title={`Prioritas ${m.label}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.color }} />
      {m.label}
    </span>
  )
}

export function PointsChip({ points }: { points?: number | null }) {
  const p = points && points > 0 ? points : 1
  return (
    <span
      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-md text-[11px] font-bold tabular-nums"
      style={{ backgroundColor: '#F0EEE7', color: '#4A4844' }}
      title={`${p} points`}
    >
      {p}
    </span>
  )
}

export function HealthBadge({ health }: { health: Health }) {
  const m = HEALTH_META[health]
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold" style={{ backgroundColor: m.bg, color: m.text }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.text }} />
      {m.label}
    </span>
  )
}

export function RiskChip({ kind }: { kind: RiskKind }) {
  const c = RISK_COLOR[kind]
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide" style={{ backgroundColor: `${c}18`, color: c }}>
      {RISK_LABEL[kind]}
    </span>
  )
}

export function Avatar({ name, size = 24 }: { name: string; size?: number }) {
  const initials = name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()
  return (
    <span
      className="inline-flex items-center justify-center rounded-full text-white font-bold flex-shrink-0"
      style={{ backgroundColor: picColor(name), width: size, height: size, fontSize: size * 0.42 }}
      title={name}
    >
      {initials}
    </span>
  )
}
